import { Inject, Injectable, Injector } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { ComponentPortal } from '@angular/cdk/portal';
import { ESCAPE } from '@angular/cdk/keycodes';
import { filter } from 'rxjs';
import { SIDE_PANEL_DATA } from '../overlay-side-panel.token';
import { ISidePanelConfiguration, ISidePanelInstance, ISidePanelRef } from '../interfaces';
import { OverlaySidePanelRegisterService } from './overlay-side-panel-register.service';

@Injectable()
export class OverlaySidePanelService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private _overlay: Overlay,
    private _injector: Injector,
    private _sidePanelRegister: OverlaySidePanelRegisterService,
  ) {}

  open(config: ISidePanelConfiguration) {
    const positionStrategy = this._overlay.position().global().right().top();

    const overlayRef: OverlayRef = this._overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'overlay-backdrop',
      panelClass: 'overlay-side-panel',
    });

    const injector = Injector.create({
      parent: this._injector,
      providers: [
        {
          provide: SIDE_PANEL_DATA,
          useValue: config.data,
        },
      ],
    });

    const sidePanelPortal = new ComponentPortal(config.component, null, injector);

    const sidePanelCompoentRef = overlayRef.attach(sidePanelPortal);
    sidePanelCompoentRef.changeDetectorRef.detectChanges();

    const sidePanel: ISidePanelInstance = {
      overlay: overlayRef,
      onClose: config.onClose,
      component: config.component,
    };

    if (config.closeOnEsc !== false) {
      const sub = overlayRef
        .keydownEvents()
        .pipe(filter((key) => key['keyCode'] === ESCAPE))
        .subscribe(() => {
          this.dismiss(sidePanel);
          sub.unsubscribe();
        });
    }

    this._sidePanelRegister.add(sidePanel);
    return this.getSidePanelRef(sidePanel);
  }

  dismiss(error?: any) {
    this._sidePanelRegister.dismissLast(error);
  }

  close(result?: any) {
    this._sidePanelRegister.closeLast(result);
  }

  private getSidePanelRef(sidePanel: ISidePanelInstance): ISidePanelRef {
    if (!sidePanel.sidePanelRef) {
      sidePanel.sidePanelRef = {
        closed: new Promise((res) => (sidePanel.resolve = res)),
        dismissed: new Promise((res) => (sidePanel.reject = res)),
      };
    }
    return sidePanel.sidePanelRef;
  }
}
